import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { EvidenceThumb, PhotoUpload } from "../components/Evidence";
import { toast } from "sonner";
import { Plus, X, SlidersHorizontal, Rows, GridFour, UploadSimple } from "@phosphor-icons/react";
import ExpiryOverview from "../components/training/ExpiryOverview";
import ClientRequirements from "../components/training/ClientRequirements";
import ClientCompliance from "../components/training/ClientCompliance";
import PolicyMatrixGroup from "../components/PolicyMatrixGroup";

const STATE_STYLE = {
  current: "bg-emerald-100 text-emerald-800 border-emerald-400",
  expiring: "bg-amber-300 text-slate-900 border-amber-500",
  expired: "bg-red-600 text-white border-red-700",
  missing: "bg-red-600 text-white border-red-700",
  awaiting_verification: "bg-blue-100 text-blue-800 border-blue-400",
  na: "bg-slate-200 text-slate-500 border-slate-300",
  none: "bg-white text-slate-400 border-dashed border-slate-300",
  expired_optional: "bg-slate-100 text-slate-500 border-slate-300",
};
const LABEL = { current: "Current", expiring: "Expiring", expired: "Expired", missing: "Missing",
  awaiting_verification: "Verify", na: "N/A", none: "—", expired_optional: "Expired" };
const CAT_ORDER = ["Internal Authorisations", "Chainsaw & Felling", "Aerial / Rope Access", "First Aid", "Manual Handling", "Machinery / Other"];

function groupTypes(types) {
  const by = {};
  types.forEach((ct) => { (by[ct.category || "Uncategorised"] ||= []).push(ct); });
  const cats = Object.keys(by).sort((a, b) => {
    const ia = CAT_ORDER.indexOf(a), ib = CAT_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
  });
  return cats.map((c) => [c, by[c]]);
}

// ---- shared cell editor (requirement + competency)
function CellEditor({ personId, ctId, canManage, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [cert, setCert] = useState(null);
  const load = useCallback(() => {
    api.get(`/compliance/cell?profile_id=${personId}&competency_type_id=${ctId}`).then((r) => {
      setData(r.data);
      const c = r.data.competency || {};
      setForm({ provider: c.provider || "", certificate_number: c.certificate_number || "",
        achieved_date: c.achieved_date || "", expiry_date: c.expiry_date || "", no_expiry: !!c.no_expiry, notes: c.notes || "" });
    }).catch(() => {});
  }, [personId, ctId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-slate-400 p-2">Loading…</div>;
  const c = data.competency;
  const setReq = async (level) => {
    try {
      if (level === "") { if (data.requirement.person_override_id) await api.delete(`/compliance/requirements/${data.requirement.person_override_id}`); }
      else await api.post("/compliance/requirements", { scope: "person", scope_id: personId, competency_type_id: ctId, level });
      toast.success("Requirement updated"); load(); onSaved && onSaved();
    } catch (e) { toast.error("Failed"); }
  };
  const grant = async () => {
    try { const payload = { profile_id: personId, competency_type_id: ctId, ...form }; if (cert) payload.certificate_url = cert.id;
      await api.post("/training/competencies", payload); toast.success("Added"); setCert(null); load(); onSaved && onSaved(); } catch (e) { toast.error("Failed"); }
  };
  const saveEdit = async () => {
    try { const payload = { ...form }; if (cert) payload.certificate_url = cert.id;
      await api.put(`/training/competencies/${c.id}`, payload); toast.success("Saved"); setCert(null); load(); onSaved && onSaved(); } catch (e) { toast.error("Failed"); }
  };
  const verify = async () => { try { await api.post(`/training/competencies/${c.id}/verify`); toast.success("Verified"); load(); onSaved && onSaved(); } catch (e) { toast.error("Failed"); } };
  const withdraw = async () => { try { await api.post(`/training/competencies/${c.id}/withdraw`, {}); toast.success("Withdrawn"); load(); onSaved && onSaved(); } catch (e) { toast.error("Failed"); } };
  const REQS = [["required", "Required"], ["optional", "Optional / Relevant"], ["na", "Not Required / N/A"], ["", "Inherit (dept/role)"]];
  const cur = data.requirement.person_level ?? "";
  return (
    <div className="flex flex-col gap-3" data-testid={`cell-editor-${personId}-${ctId}`}>
      <div className="font-body font-semibold text-slate-800">{data.competency_name}{data.is_authorisation ? " (authorisation)" : ""}</div>
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">Requirement {data.requirement.source ? `(currently ${data.requirement.level} via ${data.requirement.source})` : "(none set)"}</div>
        <div className="flex flex-wrap gap-1">
          {REQS.map(([v, l]) => (
            <button key={v} data-testid={`req-${v || "inherit"}-${personId}-${ctId}`} disabled={!canManage} onClick={() => setReq(v)}
              className={`px-2 h-8 rounded border-2 text-xs font-semibold ${cur === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-700"} disabled:opacity-50`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="border-t border-slate-100 pt-2">
        {c ? (
          <div className="flex flex-col gap-2">
            <div className="text-xs font-bold uppercase">{c.verification_status}{c.withdrawn_at ? " · withdrawn" : ""}</div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-slate-500">Achieved</label><Input data-testid={`cell-achieved-${ctId}`} type="date" value={form.achieved_date || ""} onChange={(e) => setForm({ ...form, achieved_date: e.target.value })} disabled={!canManage} className="h-9" /></div>
              <div><label className="text-xs text-slate-500">Expiry</label><Input data-testid={`cell-expiry-${ctId}`} type="date" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} disabled={!canManage || form.no_expiry} className="h-9" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.no_expiry} disabled={!canManage} onChange={(e) => setForm({ ...form, no_expiry: e.target.checked })} />No expiry</label>
            <Input placeholder="Provider" value={form.provider} disabled={!canManage} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="h-9" />
            <Input placeholder="Certificate number" value={form.certificate_number} disabled={!canManage} onChange={(e) => setForm({ ...form, certificate_number: e.target.value })} className="h-9" />
            <Input placeholder="Notes" value={form.notes} disabled={!canManage} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-9" />
            {c.certificate_url && <EvidenceThumb fileId={c.certificate_url} />}
            {canManage && (
              <div className="flex flex-wrap gap-2 items-center">
                <PhotoUpload category="training" label="Certificate" onUploaded={(f) => { setCert(f); toast.success("Attached — Save to keep"); }} />
                <Button data-testid={`cell-save-${ctId}`} onClick={saveEdit} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold">Save</Button>
                {c.verification_status !== "verified" && <Button data-testid={`cell-verify-${ctId}`} onClick={verify} className="h-9 bg-emerald-600 hover:bg-emerald-700 font-semibold">Verify</Button>}
                {!c.withdrawn_at && <Button data-testid={`cell-withdraw-${ctId}`} onClick={withdraw} variant="outline" className="h-9">Withdraw</Button>}
                <Link to="/audit?module=Training" className="text-xs text-slate-500 underline">History</Link>
              </div>
            )}
          </div>
        ) : canManage ? (
          <div className="flex flex-col gap-2">
            <div className="text-xs font-semibold text-slate-600">Grant / add competency</div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-slate-500">Achieved</label><Input data-testid={`cell-grant-achieved-${ctId}`} type="date" value={form.achieved_date || ""} onChange={(e) => setForm({ ...form, achieved_date: e.target.value })} className="h-9" /></div>
              <div><label className="text-xs text-slate-500">Expiry</label><Input data-testid={`cell-grant-expiry-${ctId}`} type="date" value={form.expiry_date || ""} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} disabled={form.no_expiry} className="h-9" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.no_expiry} onChange={(e) => setForm({ ...form, no_expiry: e.target.checked })} />No expiry</label>
            <Input placeholder="Provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="h-9" />
            <Input placeholder="Certificate number" value={form.certificate_number} onChange={(e) => setForm({ ...form, certificate_number: e.target.value })} className="h-9" />
            <div className="flex gap-2 items-center">
              <PhotoUpload category="training" label="Certificate" onUploaded={(f) => { setCert(f); toast.success("Attached"); }} />
              <Button data-testid={`cell-grant-${ctId}`} onClick={grant} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold">Grant</Button>
            </div>
          </div>
        ) : <div className="text-sm text-slate-400">No competency recorded.</div>}
      </div>
    </div>
  );
}

function ImportDrawer({ categories, onClose, onDone }) {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("");
  const [newCat, setNewCat] = useState("");
  const [isAuth, setIsAuth] = useState(false);
  const submit = async () => {
    const names = text.split("\n").map((s) => s.trim()).filter(Boolean);
    if (names.length === 0) { toast.error("Paste at least one competency"); return; }
    const cat = newCat.trim() || category || null;
    try {
      const r = await api.post("/config/competency-types/bulk", { items: names.map((n) => ({ name: n, category: cat, is_authorisation: isAuth })) });
      toast.success(`Added ${r.data.created.length}, skipped ${r.data.skipped.length}`);
      onDone();
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="import-drawer" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="font-display text-lg font-black">Import competencies</h3><button data-testid="import-close" onClick={onClose}><X size={20} /></button></div>
        <p className="text-xs text-slate-500 mb-2">One competency per line. Existing names are skipped automatically.</p>
        <textarea data-testid="import-text" value={text} onChange={(e) => setText(e.target.value)} rows={10} className="w-full border-2 border-slate-300 rounded p-2 text-sm font-mono" placeholder={"CS30\nUA301\nManual Handling"} />
        <div className="mt-2">
          <label className="text-xs font-semibold text-slate-600">Category for this batch</label>
          <select data-testid="import-category" className="h-9 border-2 border-slate-300 rounded px-2 w-full text-sm" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">— choose —</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Input data-testid="import-newcat" placeholder="…or type a new category" value={newCat} onChange={(e) => setNewCat(e.target.value)} className="h-9 mt-1" />
        </div>
        <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" data-testid="import-isauth" checked={isAuth} onChange={(e) => setIsAuth(e.target.checked)} /> These are internal authorisations (not certificated competencies)</label>
        <Button data-testid="import-submit" onClick={submit} className="mt-4 w-full h-10 bg-blue-600 hover:bg-blue-700 font-semibold">Import</Button>
      </div>
    </div>
  );
}

export default function TrainingPage() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("training_manager", "administrator");
  const [matrix, setMatrix] = useState(null);
  const [cell, setCell] = useState(null);
  const [editAll, setEditAll] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [view, setView] = useState(typeof window !== "undefined" && window.innerWidth < 768 ? "person" : "grid");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [expFilter, setExpFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [catsOpen, setCatsOpen] = useState(false);
  const [pq, setPq] = useState("");
  const [person, setPerson] = useState(null);
  const [tab, setTab] = useState("matrix");

  const load = useCallback(() => api.get("/training/matrix").then((r) => setMatrix(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!matrix) return <p className="text-slate-500">Loading matrix…</p>;

  const allCats = [...new Set(matrix.competency_types.map((c) => c.category).filter(Boolean))];
  const visibleTypes = matrix.competency_types.filter((ct) =>
    (!cat || ct.category === cat) && (!q || ct.name.toLowerCase().includes(q.toLowerCase())));
  const groups = groupTypes(visibleTypes);
  const cellDays = (cc) => (cc && cc.competency_id && cc.expiry_date && !cc.no_expiry)
    ? Math.ceil((new Date(cc.expiry_date) - new Date()) / 86400000) : null;
  const cellQualifies = (cc, fl) => {
    if (fl === "all") return true;
    if (!cc) return false;
    if (fl === "missing") return cc.level === "required" && !cc.competency_id;
    if (fl === "expired") return cc.state === "expired";
    const n = parseInt(fl, 10); const d = cellDays(cc);
    return d !== null && d >= 0 && d <= n;
  };
  let people = matrix.people.filter((p) => !pq || p.name.toLowerCase().includes(pq.toLowerCase()));
  if (expFilter !== "all") people = people.filter((p) => visibleTypes.some((ct) => cellQualifies(p.cells[ct.id], expFilter)));
  const soonest = (p) => { const ds = visibleTypes.map((ct) => cellDays(p.cells[ct.id])).filter((d) => d !== null); return ds.length ? Math.min(...ds) : Infinity; };
  if (sortBy === "soonest") people = [...people].sort((a, b) => soonest(a) - soonest(b));
  else if (sortBy === "latest") people = [...people].sort((a, b) => soonest(b) - soonest(a));
  else people = [...people].sort((a, b) => a.name.localeCompare(b.name));
  const selPerson = person ? matrix.people.find((p) => p.profile_id === person) : null;

  const cellDisplay = (cc) => {
    if (!cc) return { t: "—", c: "text-slate-300" };
    if (cc.level === "na") return { t: "N/A", c: "text-slate-400" };
    if (!cc.competency_id) return cc.level === "required" ? { t: "Missing", c: "bg-red-100 text-red-700" } : { t: "—", c: "text-slate-300" };
    if (cc.state === "awaiting_verification") return { t: "Verify", c: "bg-blue-100 text-blue-700" };
    if (cc.no_expiry || !cc.expiry_date) return { t: "No Expiry", c: "bg-emerald-100 text-emerald-700" };
    const f = new Date(cc.expiry_date).toLocaleDateString("en-GB");
    if (cc.state === "expired") return { t: `Exp ${f}`, c: "bg-red-100 text-red-700" };
    if (cc.state === "expiring") return { t: f, c: "bg-amber-100 text-amber-800" };
    return { t: f, c: "bg-emerald-100 text-emerald-700" };
  };
  const Badge = ({ st }) => <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${STATE_STYLE[st] || STATE_STYLE.none}`}>{LABEL[st] || "—"}</span>;

  return (
    <div className="flex flex-col gap-4">
      <PolicyMatrixGroup title="RAMS & Policies (acknowledgement compliance)" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Training & Competency Matrix</h1>
        <div className="flex gap-2">
          {tab === "matrix" && (<>
          <div className="flex rounded-lg border-2 border-slate-200 overflow-hidden">
            <button data-testid="view-grid" onClick={() => setView("grid")} className={`px-3 h-9 text-sm font-semibold flex items-center gap-1 ${view === "grid" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}><GridFour size={15} />Grid</button>
            <button data-testid="view-person" onClick={() => setView("person")} className={`px-3 h-9 text-sm font-semibold flex items-center gap-1 ${view === "person" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}><Rows size={15} />By person</button>
          </div>
          {canManage && <Button data-testid="cats-open" onClick={() => setCatsOpen(true)} variant="outline" className="h-9 font-semibold">Categories</Button>}
          {canManage && <Button data-testid="import-open" onClick={() => setImportOpen(true)} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold"><UploadSimple size={16} className="mr-1" />Import competencies</Button>}
          </>)}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b-2 border-slate-200" data-testid="training-tabs">
        {[["matrix", "Matrix"], ["expiry", "Expiry Overview"], ["requirements", "Client Requirements"], ["compliance", "Client Compliance"]].map(([v, l]) => (
          <button key={v} data-testid={`tab-${v}`} onClick={() => setTab(v)}
            className={`px-4 h-10 text-sm font-semibold border-b-2 -mb-0.5 ${tab === v ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "matrix" && <div className="flex flex-wrap gap-2 items-center">
        {view === "grid" && <>
          <Input data-testid="filter-competency" placeholder="Find competency…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-48" />
          <select data-testid="filter-category" className="h-9 border-2 border-slate-300 rounded px-2 text-sm" value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">All categories</option>{allCats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="filter-expiry" className="h-9 border-2 border-slate-300 rounded px-2 text-sm" value={expFilter} onChange={(e) => setExpFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="missing">Missing</option>
            <option value="expired">Expired</option>
            <option value="7">Expiring ≤ 7 days</option>
            <option value="14">Expiring ≤ 14 days</option>
            <option value="30">Expiring ≤ 30 days</option>
            <option value="60">Expiring ≤ 60 days</option>
            <option value="90">Expiring ≤ 90 days</option>
          </select>
          <select data-testid="sort-by" className="h-9 border-2 border-slate-300 rounded px-2 text-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Sort: Person name</option>
            <option value="soonest">Sort: Expiring soonest</option>
            <option value="latest">Sort: Expiring latest</option>
          </select>
        </>}
        <Input data-testid="filter-person" placeholder="Find person…" value={pq} onChange={(e) => setPq(e.target.value)} className="h-9 w-48" />
        <span className="text-xs text-slate-400">🟢 current · 🔵 verify · 🟡 expiring · 🔴 required & missing · ⚪ N/A · — not required</span>
      </div>}

      {tab === "expiry" && <ExpiryOverview />}
      {tab === "requirements" && <ClientRequirements />}
      {tab === "compliance" && <ClientCompliance />}

      {/* GRID VIEW */}
      {tab === "matrix" && view === "grid" && (
        <div className="bg-white border-2 border-slate-300 rounded-lg overflow-x-auto">
          <table className="text-sm border-collapse">
            <thead>
              <tr className="bg-slate-200">
                <th rowSpan={2} className="px-3 py-2 text-left font-body text-slate-600 sticky left-0 bg-slate-200 z-10 border-r border-slate-300">Employee</th>
                {groups.map(([c, list]) => <th key={c} colSpan={list.length} className="px-2 py-1 text-xs font-bold text-slate-700 border-l-2 border-slate-300 text-center">{c}</th>)}
                <th rowSpan={2} className="px-2 border-l border-slate-200"></th>
              </tr>
              <tr className="bg-slate-100">
                {groups.map(([c, list]) => list.map((ct, i) => (
                  <th key={ct.id} className={`px-2 py-2 font-body text-slate-600 text-[11px] whitespace-nowrap ${i === 0 ? "border-l-2 border-slate-300" : "border-l border-slate-200"}`}
                    title={ct.name}>{ct.code || (ct.name.length > 16 ? ct.name.slice(0, 15) + "…" : ct.name)}</th>
                )))}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.profile_id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold whitespace-nowrap sticky left-0 bg-white z-10 border-r border-slate-200">
                    <Link data-testid={`matrix-name-${p.profile_id}`} to="/people" className="text-slate-800 hover:text-blue-600">{p.name}</Link>
                  </td>
                  {groups.map(([c, list]) => list.map((ct, i) => {
                    const cc = p.cells[ct.id];
                    return (
                      <td key={ct.id} className={`px-1.5 py-1.5 text-center ${i === 0 ? "border-l-2 border-slate-200" : "border-l border-slate-100"}`}>
                        <button data-testid={`cell-${p.profile_id}-${ct.id}`} onClick={() => setCell({ personId: p.profile_id, ctId: ct.id, name: p.name })}>
                          {(() => { const d = cellDisplay(cc); return <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${d.c}`}>{d.t}</span>; })()}
                        </button>
                      </td>
                    );
                  }))}
                  <td className="px-2 py-1.5 border-l border-slate-100">
                    {canManage && <Button data-testid={`edit-all-${p.profile_id}`} onClick={() => setEditAll(p)} variant="outline" className="h-8 text-xs px-2"><SlidersHorizontal size={13} className="mr-1" />Edit All</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PERSON-CENTRIC VIEW */}
      {tab === "matrix" && view === "person" && (
        <div className="grid md:grid-cols-[260px_1fr] gap-4">
          <div className="bg-white border-2 border-slate-200 rounded-lg p-2 max-h-[70vh] overflow-y-auto">
            {people.map((p) => (
              <button key={p.profile_id} data-testid={`person-pick-${p.profile_id}`} onClick={() => setPerson(p.profile_id)}
                className={`block w-full text-left px-3 py-2 rounded text-sm ${person === p.profile_id ? "bg-slate-900 text-white" : "hover:bg-slate-100 text-slate-700"}`}>{p.name}</button>
            ))}
          </div>
          <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
            {selPerson ? (
              <div className="flex flex-col gap-3" data-testid="person-panel">
                <div className="flex items-center justify-between">
                  <Link to="/people" className="font-display text-lg font-black text-slate-900 hover:text-blue-600">{selPerson.name}</Link>
                  {canManage && <Button data-testid={`edit-all-${selPerson.profile_id}`} onClick={() => setEditAll(selPerson)} variant="outline" className="h-8 text-xs px-2"><SlidersHorizontal size={13} className="mr-1" />Edit All</Button>}
                </div>
                {groups.map(([c, list]) => (
                  <div key={c}>
                    <div className="text-xs font-bold uppercase text-slate-500 mb-1">{c}</div>
                    <div className="flex flex-col gap-1">
                      {list.map((ct) => {
                        const cc = selPerson.cells[ct.id];
                        return (
                          <button key={ct.id} data-testid={`pcell-${selPerson.profile_id}-${ct.id}`} onClick={() => setCell({ personId: selPerson.profile_id, ctId: ct.id, name: selPerson.name })}
                            className="flex items-center gap-2 text-sm border border-slate-100 rounded px-2 py-1.5 hover:bg-slate-50 text-left">
                            <Badge st={cc.state} /><span className="flex-1">{ct.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-slate-400 text-sm">Select a person to view and edit their competencies.</p>}
          </div>
        </div>
      )}

      {/* single cell editor */}
      {cell && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-center" data-testid="matrix-cell-modal" onClick={() => setCell(null)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2"><h3 className="font-display text-lg font-black">{cell.name}</h3><button data-testid="matrix-cell-close" onClick={() => setCell(null)}><X size={20} /></button></div>
            <CellEditor personId={cell.personId} ctId={cell.ctId} canManage={canManage} onSaved={load} />
          </div>
        </div>
      )}

      {/* Edit All drawer */}
      {editAll && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="matrix-editall-drawer" onClick={() => setEditAll(null)}>
          <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3"><h3 className="font-display text-lg font-black">{editAll.name} — all competencies</h3><button data-testid="matrix-editall-close" onClick={() => setEditAll(null)}><X size={20} /></button></div>
            <div className="flex flex-col gap-3">
              {groups.map(([c, list]) => (
                <div key={c}>
                  <div className="text-xs font-bold uppercase text-slate-500 mb-1">{c}</div>
                  {list.map((ct) => (
                    <details key={ct.id} className="border border-slate-200 rounded mb-1" data-testid={`editall-item-${ct.id}`}>
                      <summary className="px-3 py-2 cursor-pointer text-sm font-semibold flex items-center gap-2">
                        <Badge st={editAll.cells[ct.id].state} />{ct.name}
                      </summary>
                      <div className="p-3 border-t border-slate-100"><CellEditor personId={editAll.profile_id} ctId={ct.id} canManage={canManage} onSaved={load} /></div>
                    </details>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {importOpen && <ImportDrawer categories={allCats} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}
      {catsOpen && <CategoriesModal types={matrix.competency_types} onClose={() => setCatsOpen(false)} onChanged={load} />}
    </div>
  );
}

function CategoriesModal({ types, onClose, onChanged }) {
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState("");
  const loadCats = useCallback(() => api.get("/training/categories?include_archived=true").then((r) => setCats(r.data)).catch(() => {}), []);
  useEffect(() => { loadCats(); }, [loadCats]);
  const create = async () => {
    if (!newName.trim()) return;
    try { await api.post("/training/categories", { name: newName.trim() }); setNewName(""); loadCats(); onChanged?.(); toast.success("Category created"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const rename = async (c) => {
    const name = window.prompt("Rename category", c.name); if (!name || name === c.name) return;
    try { await api.put(`/training/categories/${c.id}`, { name }); loadCats(); onChanged?.(); toast.success("Renamed — updates everywhere"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggle = async (c) => { await api.put(`/training/categories/${c.id}`, { is_active: !c.is_active }); loadCats(); onChanged?.(); };
  const move = async (ctId, category_id) => { await api.post(`/training/competency-types/${ctId}/category`, { category_id: category_id || null }); onChanged?.(); toast.success("Moved"); };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="cats-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="font-display text-lg font-black">Training Categories</h3><button data-testid="cats-close" onClick={onClose}><X size={20} /></button></div>
        <div className="flex gap-2 mb-3">
          <Input data-testid="cat-new-name" placeholder="New category name" value={newName} onChange={(e) => setNewName(e.target.value)} className="h-9" />
          <Button data-testid="cat-create" onClick={create} className="h-9 bg-blue-600 hover:bg-blue-700">Add</Button>
        </div>
        <div className="flex flex-col gap-1 mb-4">
          {cats.map((c) => (
            <div key={c.id} data-testid={`cat-row-${c.id}`} className={`flex items-center justify-between border rounded px-2 py-1.5 ${c.is_active ? "border-slate-200" : "border-slate-200 bg-slate-50 opacity-60"}`}>
              <span className="text-sm font-semibold">{c.name} <span className="text-xs text-slate-400">({c.type_count})</span>{!c.is_active && <span className="text-xs text-slate-400"> · archived</span>}</span>
              <span className="flex gap-1">
                <Button data-testid={`cat-rename-${c.id}`} onClick={() => rename(c)} variant="outline" className="h-7 text-xs px-2">Rename</Button>
                <Button data-testid={`cat-toggle-${c.id}`} onClick={() => toggle(c)} variant="outline" className="h-7 text-xs px-2">{c.is_active ? "Archive" : "Restore"}</Button>
              </span>
            </div>
          ))}
        </div>
        <h4 className="font-display font-bold text-sm mb-1">Move competency types</h4>
        <div className="flex flex-col gap-1">
          {types.map((ct) => (
            <div key={ct.id} className="flex items-center justify-between gap-2 text-sm border-t border-slate-100 py-1">
              <span className="truncate">{ct.name}</span>
              <select data-testid={`move-${ct.id}`} className="h-8 border-2 border-slate-200 rounded px-1 text-xs shrink-0" value={ct.category_id || ""} onChange={(e) => move(ct.id, e.target.value)}>
                <option value="">— none —</option>
                {cats.filter((c) => c.is_active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
